import type { ReactNode } from 'react'
import { Fragment, useCallback, useContext, useMemo, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'
import { rem } from 'polished'
import moment from 'moment'

import LayoutContainer from 'components/Common/LayoutContainer'
import TextButton from 'LuxKit/Button/TextButton'
import LineAngleRightIcon from 'LuxKit/Icons/line/LineAngleRightIcon'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import Places, { getDefaultRegionPlace, getPlaceByRegionCode } from 'constants/places'
import GeoContext from 'contexts/geoContext'
import ExperiencesSearchDateQuickChips, {
  CHIP_ANYTIME,
  type ExperiencesDateQuickChipSelectPayload,
} from 'components/Search/Experiences/ExperiencesSearchDateQuickChips'
import ExperienceCompactSearchTile from 'components/Experiences/ExperienceTiles/ExperienceCompactSearchTile'
import ExperienceCompactSearchTileSkeleton from 'components/Experiences/ExperienceTiles/ExperienceCompactSearchTileSkeleton'
import useExperienceDateDisplayValue from 'components/Experiences/hooks/useExperienceDateDisplayValue'
import useSmartExperiencePlace from 'components/Experiences/hooks/useSmartExperiencePlace'
import { OfferListEventsProvider } from 'components/OfferList/OfferListEventsContext'
import useOfferListTracking from 'hooks/Offers/useOfferListTracking'
import useExperienceSearch from 'hooks/Experiences/useExperienceSearch'
import { usePlaceAncestorsById } from 'hooks/usePlace'
import { getListName } from 'analytics/enhanced-ecommerce'
import { EmptyArray, fillArray, without } from 'lib/array/arrayUtils'
import { getExperiencePlaceShortName, replaceExperienceQueryString } from 'lib/experiences/experienceUtils'
import { getExperiencesHomepageUpliftListName } from './experiencesHomepageUpliftAnalytics'

const OFFERS_LIMIT = 8
const MIN_OFFERS_TO_SHOW = 1

const messages = defineMessages({
  sectionTitle: {
    id: 'dUy76ZVut6i9szWmbuGq',
    defaultMessage: 'Top picks for <i>you</i> in {placeName}',
    description: 'Section heading for personalised top experience picks on the Experiences homepage uplift',
  },
  viewAllCta: {
    id: 'OHPZKE9oAaAK5bYnIJ0f',
    defaultMessage: 'View all experiences in {placeName}',
    description: 'Primary call-to-action linking to experience search results for the customer location on homepage uplift',
  },
  noOffersForFilter: {
    id: 'LNFt2KoC-93hKsA2owWR',
    defaultMessage: 'No experiences available for {dateLabel}',
    description: 'Empty state in Top Picks when the selected date quick chip returns no experiences for the location',
  },
})

const TileGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${rem(16)};

  ${mediaQueryUp.tablet} {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    align-items: stretch;
    gap: ${rem(36)} ${rem(16)};
  }
`

const EmptyFilterState = styled(Group)`
  padding: ${rem(24)} 0;
`

const CtaRow = styled(Group)`
  padding-top: ${rem(24)};

  ${mediaQueryUp.tablet} {
    padding-top: ${rem(36)};
  }
`

function ExperiencesHomepageTopPicks() {
  const intl = useIntl()
  const geo = useContext(GeoContext)
  const smartPlace = useSmartExperiencePlace()
  const place = smartPlace ?? getDefaultRegionPlace(geo.currentRegionCode)
  const [checkIn, setCheckIn] = useState<string | undefined>()
  const [checkOut, setCheckOut] = useState<string | undefined>()

  const placeNameShort = useMemo(
    () => getExperiencePlaceShortName(place),
    [place],
  )

  const regionPlace = useMemo(
    () => getPlaceByRegionCode(geo.currentRegionCode),
    [geo.currentRegionCode],
  )
  const [ancestorPlaceIds, fetchingAncestors] = usePlaceAncestorsById(place.id)

  const fallbackPlaceIds = useMemo(() => {
    const ancestorIds = without(ancestorPlaceIds, Places.Anywhere.id)
    return [...new Set([...ancestorIds, regionPlace.id])]
  }, [ancestorPlaceIds, regionPlace])

  const checkinDate = useMemo(
    () => (checkIn ? moment(checkIn, ISO_DATE_FORMAT) : undefined),
    [checkIn],
  )
  const checkoutDate = useMemo(
    () => (checkOut ? moment(checkOut, ISO_DATE_FORMAT) : undefined),
    [checkOut],
  )
  const selectedDateFilterLabel = useExperienceDateDisplayValue(checkinDate, checkoutDate)

  const handleDateChipSelect = useCallback((payload: ExperiencesDateQuickChipSelectPayload) => {
    if (payload.value === CHIP_ANYTIME || !payload.range) {
      setCheckIn(undefined)
      setCheckOut(undefined)
      return
    }
    setCheckIn(payload.range.start.format(ISO_DATE_FORMAT))
    setCheckOut(payload.range.end.format(ISO_DATE_FORMAT))
  }, [])

  const [list, experiences] = useExperienceSearch(
    {
      placeId: place.id,
      limit: OFFERS_LIMIT,
      sortBy: 'type',
      from: checkIn,
      to: checkOut,
      fallbackPlaceIds: fetchingAncestors ? EmptyArray : fallbackPlaceIds,
    },
  )

  const tracking = useOfferListTracking(
    getListName('OfferList', 'EXPERIENCES', getExperiencesHomepageUpliftListName('top_picks')),
    { key: place.id },
  )

  const searchUrl = useMemo(() => {
    const query = replaceExperienceQueryString({
      destinationId: place.id,
      destinationName: placeNameShort,
      ...(checkIn && { checkIn }),
      ...(checkOut && { checkOut }),
    })
    return `/search/experiences?${query}`
  }, [place, placeNameShort, checkIn, checkOut])

  const tileDate = checkIn && checkIn === checkOut ? checkIn : undefined
  const isFetching = !list || list.fetching
  const hasEnoughOffers = !!list && !list.fetching && list.ids.length >= MIN_OFFERS_TO_SHOW
  const isAnytime = !checkIn && !checkOut
  const showEmptyFilterState =
    !isFetching && !isAnytime && list && list.ids.length === 0

  if (!isFetching && isAnytime && !hasEnoughOffers) {
    return null
  }

  const noOffersForFilterLabel = intl.formatMessage(messages.noOffersForFilter, {
    dateLabel: selectedDateFilterLabel ?? '',
  })

  return (
    <LayoutContainer>
      <Group direction="vertical" gap={20}>
        <Group direction="vertical" gap={20}>
          <Heading variant="heading3">{intl.formatMessage(messages.sectionTitle, {
            placeName: placeNameShort,
            i: (chunks: ReactNode) => <i key="italic-you">{chunks}</i>,
          })}
          </Heading>
          <ExperiencesSearchDateQuickChips
            onSelect={handleDateChipSelect}
            checkinDate={checkinDate}
            checkoutDate={checkoutDate}
            dateSearchOptionId={!checkIn && !checkOut ? DATE_SEARCH_OPTION_IDS.ANYTIME : undefined}
            displayType="single-line"
            showNextBooking={false}
            showToday
          />
        </Group>
        <Group direction="vertical" gap={0}>
          <OfferListEventsProvider tracking={tracking}>
            {showEmptyFilterState ? (
              <EmptyFilterState direction="vertical" horizontalAlign="center" fullWidth>
                <Heading variant="heading4" colour="neutral-two">
                  {noOffersForFilterLabel}
                </Heading>
              </EmptyFilterState>
            ) : (
              <TileGrid>
                {isFetching && fillArray(OFFERS_LIMIT).map((id) => (
                  <ExperienceCompactSearchTileSkeleton key={id} />
                ))}
                {!isFetching && experiences.map((experience, idx) => (
                  <Fragment key={`${experience?.id ?? 'skeleton'}-${idx}`}>
                    {!experience && <ExperienceCompactSearchTileSkeleton />}
                    {experience && (
                      <ExperienceCompactSearchTile
                        experience={experience}
                        position={idx}
                        date={tileDate}
                        listKey={list.listKey}
                      />
                    )}
                  </Fragment>
                ))}
              </TileGrid>
            )}
          </OfferListEventsProvider>
          <CtaRow direction="horizontal" horizontalAlign="center" fullWidth>
            <TextButton
              kind="primary"
              size="large"
              fit="mobile-full-width"
              to={searchUrl}
              endIcon={<LineAngleRightIcon />}
            >
              {intl.formatMessage(messages.viewAllCta, {
                placeName: placeNameShort,
              })}
            </TextButton>
          </CtaRow>
        </Group>
      </Group>
    </LayoutContainer>
  )
}

export default ExperiencesHomepageTopPicks
